
import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { hasReachedClientLimit, hasClientReachedProgramLimit, isCoachBetaTier, isCoachFounder } from '@/services/client/clientService';

export function usePermissionCheck() {
  const { toast } = useToast();
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(false);
  const [isBetaTier, setIsBetaTier] = useState(false);
  const [isFounder, setIsFounder] = useState(false);
  const [canAddClient, setCanAddClient] = useState(true); 
  
  const checkPermissions = useCallback(async () => {
    setIsCheckingPermissions(true);
    try {
      // Check if user is a founder (no restrictions)
      const founderStatus = await isCoachFounder();
      setIsFounder(founderStatus);
      
      if (founderStatus) {
        setCanAddClient(true);
        setIsBetaTier(false);
        setIsCheckingPermissions(false);
        return;
      }
      
      // Check beta tier for non-founders
      const betaTier = await isCoachBetaTier();
      setIsBetaTier(betaTier);
      
      if (betaTier) {
        // Check client limit only for beta tier users
        const reachedLimit = await hasReachedClientLimit();
        setCanAddClient(!reachedLimit);
      } else {
        // Non-Beta users can add clients freely
        setCanAddClient(true);
      }
    } catch (error) {
      console.error('Error checking permissions:', error);
      // Default to true if there's an error to prevent false restrictions
      setCanAddClient(true); 
    } finally {
      setIsCheckingPermissions(false);
    }
  }, []);
  
  // Check permissions on mount
  useEffect(() => {
    checkPermissions();
  }, [checkPermissions]);
  
  const checkCanAddProgramToClient = async (clientId: string | undefined) => {
    if (!clientId) return false;
    
    setIsCheckingPermissions(true);
    try {
      // Check if user is a founder (no restrictions)
      if (await isCoachFounder()) {
        return true;
      }
      
      // Check if user is on beta tier
      if (await isCoachBetaTier()) {
        // Use the database-enforced program limit check
        const reachedLimit = await hasClientReachedProgramLimit(clientId);
        
        if (reachedLimit) {
          // Only show toast when checking if a new program can be added
          toast({
            title: "Program limit reached",
            description: "Beta tier clients can have at most 2 programs",
            variant: "destructive"
          });
          return false;
        }
      }
      return true;
    } catch (error) {
      console.error('Error checking program limit:', error);
      toast({
        title: "Error checking limits",
        description: "Could not verify if more programs can be added",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsCheckingPermissions(false);
    }
  };
  
  return {
    isCheckingPermissions,
    isBetaTier,
    isFounder,
    canAddClient,
    checkCanAddProgramToClient,
    refreshPermissions: checkPermissions
  };
}
